import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Download } from "lucide-react";
import { useApp, formatMoney, useCanEdit } from "@/lib/app-context";
import { monthLabel, shiftMonth, currentMonth, groupTotals, combineGroups, exportExcel } from "@/lib/sheet-utils";
import { ExchangeRateButton } from "@/components/ExchangeRateButton";
import { SearchBox, matches } from "@/components/SearchBox";
import { Button } from "@/components/ui/button";
import { SalaryTable } from "@/components/SalaryTable";
import { SalaryCards } from "@/components/SalaryCards";
import { PayslipDialog } from "@/components/PayslipDialog";

const Stat = ({ label, value, tone, testId }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-4">
    <div className="text-xs font-semibold text-slate-500">{label}</div>
    <div className={`font-mono text-lg sm:text-xl font-extrabold mt-1 space-y-0.5 ${tone || ""}`} data-testid={testId}>{value}</div>
  </div>
);

export default function SalarySheet() {
  const { api } = useApp();
  const canEdit = useCanEdit();
  const [month, setMonth] = useState(currentMonth());
  const [rows, setRows] = useState(null);
  const [slip, setSlip] = useState(null);
  const [q, setQ] = useState("");
  const [fx, setFx] = useState(null);

  useEffect(() => {
    api("get", "/settings").then(setFx).catch(() => {});
  }, [api]);

  const saveFx = async (body) => {
    try {
      setFx(await api("put", "/settings", body));
      toast.success(body.aed_to_inr ? "Exchange rate saved" : "Exchange rate removed");
      return true;
    } catch {
      toast.error("Please enter a valid rate");
      return false;
    }
  };

  const load = useCallback(async () => {
    setRows(null);
    try {
      setRows(await api("get", `/sheets/${month}`));
    } catch {
      setRows([]);
      toast.error("Could not load this month");
    }
  }, [api, month]);
  useEffect(() => { load(); }, [load]);

  const groups = useMemo(() => groupTotals(rows || []), [rows]);
  const combined = useMemo(() => combineGroups(groups, fx), [groups, fx]);
  const multi = (fn) => (groups.length ? [
    ...groups.map((g) => <div key={g.code}>{formatMoney(fn(g.t), g.code)}</div>),
    combined && <div key="all" className="text-xs text-slate-500 font-semibold pt-0.5" data-testid="stat-combined">≈ {formatMoney(fn(combined.t), combined.code)} combined</div>,
  ] : formatMoney(0, "INR"));

  const patch = async (row, changes) => {
    try {
      const updated = await api("patch", `/entries/${row.id}`, changes);
      setRows((rs) => rs.map((r) => (r.id === row.id ? updated : r)));
    } catch {
      toast.error("Could not save the change");
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Remove ${row.staff_name} from ${monthLabel(month)} only?`)) return;
    try {
      await api("delete", `/entries/${row.id}`);
      setRows((rs) => rs.filter((r) => r.id !== row.id));
    } catch {
      toast.error("Could not remove");
    }
  };

  const shown = (rows || []).filter((r) => matches(q, r.staff_name, r.agent_name, r.note));
  const props = { rows: shown, groups: combined ? [...groups, combined] : groups, onPatch: patch, onSlip: setSlip, onRemove: remove };

  return (
    <div className="space-y-6 fade-up" data-testid="salary-sheet-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-teal-700">Monthly salary sheet</div>
          <div className="flex flex-wrap items-center gap-2 mt-1">
            <button onClick={() => setMonth(shiftMonth(month, -1))} className="p-1.5 rounded-lg hover:bg-white border border-transparent hover:border-slate-200" data-testid="prev-month-button"><ChevronLeft size={20} /></button>
            <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight min-w-[170px] text-center" data-testid="month-title">{monthLabel(month)}</h1>
            <button onClick={() => setMonth(shiftMonth(month, 1))} className="p-1.5 rounded-lg hover:bg-white border border-transparent hover:border-slate-200" data-testid="next-month-button"><ChevronRight size={20} /></button>
            <input type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} className="ml-1 h-9 px-2 rounded-lg border border-slate-200 bg-white text-sm" data-testid="month-picker" />
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {canEdit && <ExchangeRateButton fx={fx} onSave={saveFx} />}
          <Button onClick={() => exportExcel(rows || [], month, combined, fx)} disabled={!rows?.length} variant="outline" className="border-slate-300" data-testid="export-excel-button">
            <Download size={16} />Download Excel
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Stat label="Earned this month" value={multi((t) => t.earned)} testId="stat-salary" />
        <Stat label="Bonus" value={multi((t) => t.bonus)} tone="text-emerald-700" testId="stat-bonus" />
        <Stat label="Advance + deduct" value={multi((t) => t.advance + t.deduct)} tone="text-red-600" testId="stat-cuts" />
        <Stat label="Payable" value={multi((t) => t.payable)} tone="text-teal-700" testId="stat-payable" />
        <Stat label="Balance to next month" value={multi((t) => t.balance)} tone="text-amber-600" testId="stat-pending" />
      </div>

      {rows === null ? (
        <div className="text-slate-500 text-sm" data-testid="sheet-loading">Loading sheet…</div>
      ) : rows.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-xl p-10 text-center" data-testid="sheet-empty">
          <p className="text-slate-600">No staff for this month yet.</p>
          <Link to="/staff" className="inline-block mt-3 text-teal-700 font-semibold underline" data-testid="go-add-staff-link">Add staff to your list</Link>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <SearchBox value={q} onChange={setQ} placeholder="Search staff, agent or note…" testId="sheet-search" />
            {q && <span className="text-xs text-slate-500" data-testid="sheet-search-count">Showing {shown.length} of {rows.length} · totals are for the whole month</span>}
          </div>
          {q && shown.length === 0 && <div className="bg-white border border-dashed border-slate-300 rounded-xl p-6 text-center text-slate-500" data-testid="sheet-no-match">No staff match “{q}”.</div>}
          <SalaryTable {...props} />
          <SalaryCards {...props} />
          <p className="text-xs text-slate-500">Month = 30 days. Earned = Salary × Days ÷ 30 (joining month counts from the joining date). Payable = Earned + B/F + Bonus − Advance − Deduct. Balance c/f = Payable − Paid, carried to next month (minus = receivable from staff). {canEdit ? "Tap a number to change it; changes save automatically." : "You have view-only access."}</p>
        </>
      )}
      {slip && <PayslipDialog entry={slip} currency={slip.currency || "INR"} onClose={() => setSlip(null)} />}
    </div>
  );
}
